// components/AdminPortal/LearningPathTable.jsx
import React, { useState } from "react";
import {
  Box,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  Text,
  Button,
  DropButton,
  Layer,
  Heading,
  Notification,
  TextInput,
} from "grommet";
import { Archive, Edit, Upload, View, More } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import StatusDot from "./StatusDot";
import axios from "axios";

const LearningPathTable = ({ skillData = [], portfolio_id, onEdit }) => {
  const navigate = useNavigate();

  // Edit name modal
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [editedName, setEditedName] = useState("");

  // Publish/unpublish confirmation modals
  const [confirmPublishVisible, setConfirmPublishVisible] = useState(false);
  const [confirmUnpublishVisible, setConfirmUnpublishVisible] = useState(false);

  // Dropdown visibility
  const [activeDropId, setActiveDropId] = useState(null);

  // Notification
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    status: "normal",
  });

  const sortedskillData = [...skillData].sort((a, b) => a.status - b.status);

  const openEditModal = (skill) => {
    setSelectedSkill(skill);
    setEditedName(skill.learning_path_name);
    setEditModalVisible(true);
    setActiveDropId(null);
  };

  const handleSaveName = async () => {
    try {
      const res = await axios.patch(
        `/api/admin/update-learning-path-name/${selectedSkill.learning_path_id}`,
        { name: editedName },
      );
      setNotification({
        show: true,
        message: res.data.message || "Name updated",
        status: "normal",
      });
      setEditModalVisible(false);
      onEdit({ ...selectedSkill, learning_path_name: editedName });
      setTimeout(() => {
        window.location.reload();
      }, 2000); // Reload the page after 2 seconds
    } catch (err) {
      setNotification({
        show: true,
        message: err.response?.data?.error || "Error updating name",
        status: "critical",
      });
    }
  };

  const openUnpublishModal = (skill) => {
    setSelectedSkill(skill);
    setConfirmUnpublishVisible(true);
    setActiveDropId(null);
  };

  const handleConfirmUnpublish = async () => {
    try {
      const res = await axios.patch(
        `/api/admin/unpublish-learning-path/${selectedSkill.learning_path_id}`,
      );
      setNotification({
        show: true,
        message: res.data.message || "Learning Path Unpublished successfully",
        status: "normal",
      });
      setConfirmUnpublishVisible(false);
      onEdit({ ...selectedSkill, status: 0 });
      setTimeout(() => {
        window.location.reload();
      }, 2000); // Reload the page after 2 seconds
    } catch (err) {
      setNotification({
        show: true,
        message: err.response?.data?.error || "Error unpublishing",
        status: "critical",
      });
    }
  };

  const openPublishModal = (skill) => {
    setSelectedSkill(skill);
    setConfirmPublishVisible(true);
    setActiveDropId(null);
  };

  const handleConfirmPublish = async () => {
    try {
      const res = await axios.patch(
        `/api/admin/publish-learning-path/${selectedSkill.learning_path_id}`,
      );
      setNotification({
        show: true,
        message: res.data.message || "Learning Path Published successfully",
        status: "normal",
      });
      setConfirmPublishVisible(false);
      onEdit({ ...selectedSkill, status: 1 });
      setTimeout(() => {
        window.location.reload();
      }, 2000); // Reload the page after 2 seconds
    } catch (err) {
      setNotification({
        show: true,
        message: err.response?.data?.error || "Error publishing",
        status: "critical",
      });
    }
  };

  return (
    <Box
      overflow="auto"
      background="light-1"
      round="small"
      pad="small"
      onScroll={() => setActiveDropId(null)}
    >
      {notification.show && (
        <Notification
          toast
          status={notification.status}
          message={notification.message}
          onClose={() => setNotification((n) => ({ ...n, show: false }))}
        />
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableCell rowSpan="2">
              <Text weight="bold">Name</Text>
            </TableCell>
            <TableCell colSpan="4" align="center">
              <Text weight="bold">Courses</Text>
            </TableCell>
            <TableCell rowSpan="2" align="center">
              <Text weight="bold">Actions</Text>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <Text weight="bold">Prerequisite</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Foundation</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Intermediate</Text>
            </TableCell>
            <TableCell>
              <Text weight="bold">Total</Text>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {skillData.length === 0 ? (
            <TableRow>
              <TableCell colSpan="5" align="center">
                No skill data found.
              </TableCell>
            </TableRow>
          ) : (
            sortedskillData.map((skill) => {
              const isPublished = skill.status === 1;
              return (
                <TableRow key={skill.learning_path_id}>
                  <TableCell>
                    <Box direction="row" align="center" gap="small">
                      <StatusDot status={skill.status} />
                      <Text>{skill.learning_path_name}</Text>
                    </Box>
                  </TableCell>
                  <TableCell>{skill.prerequisite}</TableCell>
                  <TableCell>{skill.foundation}</TableCell>
                  <TableCell>{skill.intermediate}</TableCell>
                  <TableCell>{skill.total_courses}</TableCell>
                  <TableCell align="center">
                    <DropButton
                      icon={<More color="brand" />}
                      dropAlign={{
                        top: "bottom",
                        right: "right",
                      }}
                      open={activeDropId === skill.learning_path_id}
                      onOpen={() => setActiveDropId(skill.learning_path_id)}
                      onClose={() => setActiveDropId(null)}
                      dropContent={
                        <Box pad="small" gap="small" background="light-2">
                          <Button
                            hoverIndicator
                            onClick={() =>
                              navigate(
                                `/admin/portfolio/${portfolio_id}/learning-path/${skill.learning_path_id}/courses`,
                              )
                            }
                          >
                            <Box
                              direction="row"
                              gap="small"
                              align="center"
                              pad="xsmall"
                            >
                              <View color="brand" />
                              <Text>View Courses</Text>
                            </Box>
                          </Button>
                          <Button
                            hoverIndicator
                            onClick={() => openEditModal(skill)}
                          >
                            <Box
                              direction="row"
                              gap="small"
                              align="center"
                              pad="xsmall"
                            >
                              <Edit color="brand" />
                              <Text>Edit Name</Text>
                            </Box>
                          </Button>
                          {isPublished ? (
                            <Button
                              hoverIndicator
                              onClick={() => openUnpublishModal(skill)}
                            >
                              <Box
                                direction="row"
                                gap="small"
                                align="center"
                                pad="xsmall"
                              >
                                <Archive color="status-critical" />
                                <Text>Unpublish</Text>
                              </Box>
                            </Button>
                          ) : (
                            <Button
                              hoverIndicator
                              onClick={() => openPublishModal(skill)}
                            >
                              <Box
                                direction="row"
                                gap="small"
                                align="center"
                                pad="xsmall"
                              >
                                <Upload color="brand" />
                                <Text>Publish</Text>
                              </Box>
                            </Button>
                          )}
                        </Box>
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {editModalVisible && (
        <Layer
          onEsc={() => setEditModalVisible(false)}
          onClickOutside={() => setEditModalVisible(false)}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Heading level="3" margin="none">
              Edit Learning Path Name
            </Heading>
            <TextInput
              placeholder="Enter new name"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
            />
            <Box direction="row" gap="small" justify="end">
              <Button
                label="Cancel"
                onClick={() => setEditModalVisible(false)}
              />
              <Button
                label="Save"
                primary
                onClick={handleSaveName}
                disabled={editedName.trim() === ""}
              />
            </Box>
          </Box>
        </Layer>
      )}

      {confirmUnpublishVisible && selectedSkill && (
        <Layer
          onEsc={() => setConfirmUnpublishVisible(false)}
          onClickOutside={() => setConfirmUnpublishVisible(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level="3" margin="none">
              Unpublish Learning Path
            </Heading>
            <Text>
              Are you sure you want to unpublish{" "}
              <strong>{selectedSkill.learning_path_name}</strong>?
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button
                label="No"
                onClick={() => setConfirmUnpublishVisible(false)}
              />
              <Button label="Yes" primary onClick={handleConfirmUnpublish} />
            </Box>
          </Box>
        </Layer>
      )}

      {confirmPublishVisible && selectedSkill && (
        <Layer
          onEsc={() => setConfirmPublishVisible(false)}
          onClickOutside={() => setConfirmPublishVisible(false)}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level="3" margin="none">
              Publish Learning Path
            </Heading>
            <Text>
              Are you sure you want to publish{" "}
              <strong>{selectedSkill.learning_path_name}</strong>?
            </Text>
            <Box direction="row" justify="end" gap="small">
              <Button
                label="No"
                onClick={() => setConfirmPublishVisible(false)}
              />
              <Button label="Yes" primary onClick={handleConfirmPublish} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default LearningPathTable;
